/**
 * api/client.js — Updated with alerts, sync, and admin endpoints
 */

const BASE = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

export const token = {
  get:   () => localStorage.getItem("oci_token"),
  set:   (t) => localStorage.setItem("oci_token", t),
  clear: () => localStorage.removeItem("oci_token"),
};

async function request(path, options = {}) {
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  const t = token.get();
  if (t) headers["Authorization"] = `Bearer ${t}`;
  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  if (res.status === 401) {
    token.clear();
    window.dispatchEvent(new Event("auth:logout"));
    throw new Error("Session expired — please log in again");
  }
  // 204 No Content — no body to parse
  if (res.status === 204) return null;

  const body = res.headers.get("content-type")?.includes("json")
    ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = body?.detail || body || `HTTP ${res.status}`;
    throw new Error(typeof msg === "object" ? JSON.stringify(msg) : msg);
  }
  return body;
}

const get  = (path)       => request(path, { method: "GET" });
const post = (path, data) => request(path, { method: "POST",  body: JSON.stringify(data) });
const patch= (path, data) => request(path, { method: "PATCH", body: JSON.stringify(data) });
const put  = (path, data) => request(path, { method: "PUT",   body: JSON.stringify(data) });
const del  = (path)       => request(path, { method: "DELETE", skipJson: true });

async function postForm(path, formData) {
  const t = token.get();
  const headers = {};
  if (t) headers["Authorization"] = `Bearer ${t}`;
  const res = await fetch(`${BASE}${path}`, { method: "POST", headers, body: formData });
  if (res.status === 401) { token.clear(); window.dispatchEvent(new Event("auth:logout")); throw new Error("Session expired"); }
  const body = await res.json();
  if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
  return body;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const auth = {
  login: async (email, password) => {
    const form = new URLSearchParams({ username: email, password });
    const res = await fetch(`${BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form,
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.detail || "Login failed");
    token.set(body.access_token);
    return body;
  },
  me:     () => get("/auth/me"),
  logout: () => { token.clear(); },
};

// ─── Clients ──────────────────────────────────────────────────────────────────
export const clients = {
  list:   ()         => get("/clients"),
  get:    (id)       => get(`/clients/${id}`),
  create: (data)     => post("/clients", data),
  update: (id, data) => patch(`/clients/${id}`, data),
  delete: (id)       => del(`/clients/${id}`),
};

// ─── RFP ──────────────────────────────────────────────────────────────────────
export const rfp = {
  list:    (cid)         => get(`/clients/${cid}/rfp`),
  replace: (cid, lines)  => put(`/clients/${cid}/rfp`, lines),
  addLine: (cid, data)   => post(`/clients/${cid}/rfp`, data),
  update:  (cid, lid, d) => patch(`/clients/${cid}/rfp/${lid}`, d),
  delete:  (cid, lid)    => del(`/clients/${cid}/rfp/${lid}`),
};

// ─── Actuals ──────────────────────────────────────────────────────────────────
export const actuals = {
  list:   (cid, month) => get(`/clients/${cid}/actuals${month ? `?month=${month}` : ""}`),
  upsert: (cid, rows)  => post(`/clients/${cid}/actuals`, rows),
};

// ─── Ingestion ────────────────────────────────────────────────────────────────
export const ingest = {
  csv: async (cid, month, usageFile, costFile) => {
    const fd = new FormData();
    fd.append("month", month);
    fd.append("usage_file", usageFile);
    fd.append("cost_file", costFile);
    return postForm(`/clients/${cid}/ingest/csv`, fd);
  },
  ociApi: (cid, month) => post(`/clients/${cid}/ingest/oci-api?month=${month}`, {}),
};

// ─── OCI Sync ─────────────────────────────────────────────────────────────────
export const sync = {
  triggerMonth:   (cid, month) => post(`/clients/${cid}/sync/${month}`, {}),
  triggerCurrent: (cid)        => post(`/clients/${cid}/sync`, {}),
};

// ─── Analysis ─────────────────────────────────────────────────────────────────
export const analysis = {
  month:           (cid, month)  => get(`/clients/${cid}/analysis/month/${month}`),
  range:           (cid, months) => get(`/clients/${cid}/analysis/range?months=${months.join(",")}`),
  availableMonths: (cid)         => get(`/clients/${cid}/analysis/available-months`),
  compartments:    (cid, month)  => get(`/clients/${cid}/analysis/compartments/${month}`),
};

// ─── AI ───────────────────────────────────────────────────────────────────────
export const ai = {
  dashboard: (cid, months) =>
    post(`/clients/${cid}/ai/dashboard?months=${months.join(",")}`, { insight_type: "dashboard" }),
  invoice: (cid, month) =>
    post(`/clients/${cid}/ai/invoice/${month}`, {}),
  ask: (cid, question) =>
    post(`/clients/${cid}/ai/ask?question=${encodeURIComponent(question)}`, {}),
};

// ─── Catalogue ────────────────────────────────────────────────────────────────
export const catalogue = {
  list: (service) => get(`/catalogue${service ? `?service=${service}` : ""}`),
  sync: ()        => post("/catalogue/sync", {}),
};

// ─── Alerts ───────────────────────────────────────────────────────────────────
export const alerts = {
  list:    (cid)              => get(`/clients/${cid}/alerts`),
  create:  (cid, data)        => post(`/clients/${cid}/alerts`, data),
  update:  (cid, aid, data)   => patch(`/clients/${cid}/alerts/${aid}`, data),
  delete:  (cid, aid)         => del(`/clients/${cid}/alerts/${aid}`),
  history: (cid, month)       => get(`/clients/${cid}/alerts/history${month ? `?month=${month}` : ""}`),
  check:   (cid, month)       => post(`/clients/${cid}/alerts/check${month ? `?month=${month}` : ""}`, {}),
};

// ─── Admin ────────────────────────────────────────────────────────────────────
export const admin = {

  getSettings:  (cid)       => get(`/admin/clients/${cid}/settings`),
  saveOCI:      (cid, data) => post(`/admin/clients/${cid}/settings/oci`, data),
  testOCI:      (cid)       => post(`/admin/clients/${cid}/settings/oci/test`, {}),
  saveAlerts:   (cid, data) => post(`/admin/clients/${cid}/settings/alerts`, data),
  saveColumns:  (cid, data) => post(`/admin/clients/${cid}/settings/columns`, data),
  saveContract: (cid, data) => post(`/admin/clients/${cid}/settings/contract`, data),

  syncPrices: () => post("/admin/catalogue/sync-prices", {}),

  // Users
  listUsers:        ()              => get("/admin/users"),
  createUser:       (data)          => post("/admin/users", data),
  updateUser:       (id, data)      => request(`/admin/users/${id}`, { method:"PATCH", body:JSON.stringify(data) }),
  deleteUser:       (id)            => request(`/admin/users/${id}`, { method:"DELETE", skipJson:true }),

  // Data management
  dataSummary:      (cid)           => get(`/admin/clients/${cid}/data-summary`),
  deleteMonthData:  (cid, month)    => request(`/admin/clients/${cid}/data/${month}`, { method:"DELETE" }),
  listUsers:    ()             => get("/admin/users"),
  createUser:   (data)         => post("/admin/users", data),
  updateUser:   (uid, data)    => patch(`/admin/users/${uid}`, data),
  deleteUser:   (uid)          => del(`/admin/users/${uid}`),
  listClients:  ()             => get("/admin/clients"),
  syncAll:      (month)        => post(`/admin/sync-all${month ? `?month=${month}` : ""}`, {}),
  checkAlerts:  (month)        => post(`/admin/alerts/check-all${month ? `?month=${month}` : ""}`, {}),
};

// ─── Health ───────────────────────────────────────────────────────────────────
export const health = () =>
  fetch(`${BASE.replace("/api/v1", "")}/health`).then(r => r.json());
